import React, { useState } from 'react';
import { GitCompare } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * ⇄ Compare M1 vs M2 button — fires two PDF renders (`layout_override=m1`
 * and `layout_override=m2`) against `/reports/detailed-roof-appraisal/{id}`
 * and opens both in fresh tabs. Lets the operator eyeball inline-thumb
 * bullets side-by-side with the bullet-only + gallery layout before
 * choosing which to send the client.
 *
 * Used in: Roof Appraisal tab (Multi-Defect mode) and Exports panel.
 * Props:
 *   • appraisalId (required)
 *   • variant     (default 'detailed_multi' — the only variant with photo layouts)
 *   • size        (compact | normal)
 */
export const CompareLayoutsButton = ({ appraisalId, variant = 'detailed_multi', size = 'normal' }) => {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);

  const fireBoth = async () => {
    if (!appraisalId || busy) return;
    setBusy(true);
    try {
      // Fetch M1 and M2 in parallel so the operator sees both tabs open at
      // roughly the same time. If either fails we still show the other.
      const settled = await Promise.allSettled([
        api.get(`/reports/detailed-roof-appraisal/${appraisalId}`, {
          responseType: 'blob',
          params: { variant, layout_override: 'm1' },
        }),
        api.get(`/reports/detailed-roof-appraisal/${appraisalId}`, {
          responseType: 'blob',
          params: { variant, layout_override: 'm2' },
        }),
      ]);
      let popupBlocked = false;
      settled.forEach((r, idx) => {
        if (r.status === 'fulfilled') {
          const url = URL.createObjectURL(r.value.data);
          const win = window.open(url, '_blank', 'noopener,noreferrer');
          if (!win || win.closed || typeof win.closed === 'undefined') popupBlocked = true;
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else {
          const label = idx === 0 ? 'M1 (inline thumbs)' : 'M2 (bullets + gallery)';
          console.warn(`Design compare — ${label} render failed`, r.reason);
        }
      });
      if (popupBlocked) {
        alert('Preview tabs blocked. Please allow pop-ups for this site to see both layouts side-by-side.');
      }
    } finally {
      setBusy(false);
    }
  };

  const compact = size === 'compact';
  return (
    <button
      type="button"
      data-testid="compare-layouts-btn"
      onClick={fireBoth}
      disabled={busy}
      title="Open both Multi-Defect PDF layouts (M1 inline thumbs vs M2 bullets + gallery) in new tabs — pick the one you prefer"
      className={
        compact
          ? 'flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-purple-700 border border-purple-300 rounded-md hover:bg-purple-50 disabled:opacity-50 transition-colors'
          : 'flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-purple-700 border border-purple-300 rounded-md hover:bg-purple-50 disabled:opacity-50 transition-colors'
      }
    >
      <GitCompare size={compact ? 11 : 12} />
      {busy ? 'Opening…' : 'Compare M1 vs M2'}
    </button>
  );
};

export default CompareLayoutsButton;
